import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector("#contacto");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Parallax calculations - more dramatic
  const textOffset = scrollY * 0.3;
  const imageOffset = scrollY * -0.2;
  const textOpacity = Math.max(0, 1 - scrollY / 600);
  const imageScale = 1 + scrollY * 0.0003;

  return (
    <section 
      ref={sectionRef}
      id="hero" 
      data-testid="hero-section" 
      className="relative bg-white overflow-hidden min-h-screen"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-28 pb-16 relative">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-4 items-center min-h-[80vh]">
          {/* Text content - moves up faster */}
          <div 
            className="relative z-10"
            style={{
              transform: `translateY(${textOffset}px)`,
              opacity: textOpacity,
              transition: "transform 0.05s linear",
              willChange: "transform, opacity",
            }}
          >
            <p className="text-calle-primary-50 text-xs uppercase tracking-[0.3em] mb-6 font-semibold font-body">
              Especializados en recupero y cobranza judicial de créditos de nuestros clientes
            </p>
            <h1
              data-testid="hero-headline"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-[4rem] text-calle-primary leading-[1.1] tracking-[-0.01em] mb-8"
            >
              Recuperación judicial de créditos morosos para bancos e instituciones financieras.
            </h1>
            <p className="text-gray-500 text-base md:text-lg max-w-xl leading-relaxed mb-6 font-body">
              Gestionamos carteras morosas con experiencia legal, procesos eficientes, tecnología aplicada y cobertura nacional.
            </p>
            <Button
              data-testid="hero-contact-btn"
              onClick={scrollToContact}
              className="bg-calle-primary text-white hover:bg-calle-primary/90 rounded-sm px-10 py-6 text-sm font-semibold font-body shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
            >
              Contactar <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Image - moves down slower with scale */}
          <div className="relative flex justify-center lg:justify-end">
            <div 
              className="relative w-full max-w-xl overflow-hidden rounded-sm shadow-2xl"
              style={{ 
                height: "520px",
                transform: `translateY(${imageOffset}px) scale(${imageScale})`,
                transition: "transform 0.05s linear",
                willChange: "transform",
              }}
            >
              <img
                src="/images/moneda.png"
                alt="Palacio de La Moneda, Santiago de Chile"
                data-testid="hero-image"
                className="w-full h-[130%] object-cover object-center grayscale-img absolute top-0 left-0"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{ opacity: textOpacity }}
      >
        <span className="text-calle-primary/50 text-xs uppercase tracking-widest font-body">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-calle-primary/50 to-transparent animate-pulse"></div>
      </div>

      {/* Marquee */}
      <div 
        data-testid="services-marquee" 
        className="bg-calle-primary py-4 overflow-hidden"
        style={{
          transform: `translateY(${scrollY * -0.1}px)`,
          transition: "transform 0.05s linear",
        }}
      >
        <div className="animate-marquee flex items-center whitespace-nowrap">
          {["Cobranza Judicial", "Gestión de Carteras", "Negociación y Recupero", "Incobrabilidad", "Cobranza Judicial", "Gestión de Carteras", "Negociación y Recupero", "Incobrabilidad"].map((item, i) => (
            <span key={`marquee-${i}`} className="flex items-center">
              <span className="text-white font-medium text-lg mx-8 tracking-wide font-body">{item}</span>
              <span className="text-white/40 text-xl mx-2">&#10038;</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
